import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { createPopper } from '@popperjs/core';
import { FormValidators } from '../../validators';
import {
  IndexedDbService,
  MeasurementUtil,
  NumericInputDirective,
  SessionService,
} from '../../core';
import { MeasurementSystemEnum } from '@cai-services';
import { cloneDeep } from 'lodash';

@Component({
  selector: 'common-rates-load-types-input',
  imports: [FormsModule, ReactiveFormsModule, NumericInputDirective],
  templateUrl: './rates-load-types-input.component.html',
  styleUrl: './rates-load-types-input.component.scss',
  standalone: true,
})
export class RatesLoadTypesInputComponent {
  @ViewChild('volumeUnitInput', { static: false }) volumeUnitInput!: ElementRef;
  @ViewChild('volumeUnitPopover', { static: false })
  volumeUnitPopover!: ElementRef;
  @ViewChild('weightUnitInput', { static: false }) weightUnitInput!: ElementRef;
  @ViewChild('weightUnitPopover', { static: false })
  weightUnitPopover!: ElementRef;
  loadType = 'BY_VOLUME';
  weightUnitPopoverVisible = false;
  selectedWeightUnit = 'kg';
  selectedDimensionWeightUnit = 'kg';
  selectedDimensionUnit = 'cm';

  weightUnitPopperInstance: any;
  selectedVolumeUnit = 'm3';
  volumeUnitPopoverVisible = false;
  volumeUnitPopperInstance: any;
  totalLoadTypeForm: FormGroup;
  dimensionLoadTypeForm: FormGroup;
  previousData: any;
  isVolumeLow: boolean;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private readonly indexedDbService: IndexedDbService,
    private readonly sessionService: SessionService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  async ngOnInit() {
    const { measurementSystem } = this.sessionService.getCurrentUser();

    this.selectedWeightUnit = measurementSystem === 'IMPERIAL' ? 'lb' : 'kg';
    this.selectedDimensionWeightUnit =
      measurementSystem === 'IMPERIAL' ? 'lb' : 'kg';
    this.selectedDimensionUnit =
      measurementSystem === 'IMPERIAL' ? 'inch' : 'cm';
    this.selectedVolumeUnit = measurementSystem === 'IMPERIAL' ? 'ft3' : 'm3';
    this.totalLoadTypeForm = this.fb.group({
      pieces: [
        '',
        [
          FormValidators.required(),
          FormValidators.greaterThan(1),
          FormValidators.lessThan(1000),
        ],
      ],
      volume: [
        '',
        [
          FormValidators.required(),
          FormValidators.greaterThan(0.0001),
          FormValidators.noDecimalPoint(),
          FormValidators.lessThan(
            MeasurementUtil.convertVolume(
              1000,
              MeasurementSystemEnum.METRIC,
              measurementSystem,
            ),
            this.selectedVolumeUnit,
          ),
        ],
      ],
      volumeType: [this.selectedVolumeUnit, FormValidators.required()],
      weight: [
        '',
        [
          FormValidators.required(),
          FormValidators.noDecimalPoint(),
          FormValidators.greaterThan(0.1),
          FormValidators.lessThan(
            MeasurementUtil.convertWeight(
              1000000,
              MeasurementSystemEnum.METRIC,
              measurementSystem,
            ),
            this.selectedWeightUnit,
          ),
        ],
      ],
      weightType: [this.selectedWeightUnit, FormValidators.required()],
    });

    this.dimensionLoadTypeForm = this.fb.group({
      dimensions: this.fb.array([this.createDimensionGroup()]),
    });

    this.previousData =
      await this.indexedDbService.getFormData('shipmentDimensions');

    this.dimensionLoadTypeForm.get('dimensions').valueChanges.subscribe(() => {
      this.checkIfVolumeLow();
    });
    this.cdr.detectChanges();
  }

  checkIfVolumeLow(): void {
    const volume = this.calculateDimensionVolume();

    const convertedVolume = MeasurementUtil.convertVolume(
      +volume,
      this.selectedDimensionUnit === 'inch'
        ? MeasurementSystemEnum.IMPERIAL
        : MeasurementSystemEnum.METRIC,

      MeasurementSystemEnum.METRIC,
    );

    if (+convertedVolume < 0.0001) {
      this.isVolumeLow = true;
    } else {
      this.isVolumeLow = false;
    }
  }

  handleLoadTypeChange(value: string) {
    if (value === this.loadType) {
      return;
    }
    this.loadType = value;
    if (this.loadType === 'BY_VOLUME') {
      this.isVolumeLow = false;
    } else if (
      this.dimensionLoadTypeForm?.dirty ||
      this.dimensionLoadTypeForm?.touched
    ) {
      this.checkIfVolumeLow();
    }
  }

  calculateDimensionVolume(): string {
    return this.dimensionLoadTypeForm
      ?.get('dimensions')
      ?.value?.map((dimension) => {
        return MeasurementUtil.calculateVolume(
          dimension.width,
          dimension.length,
          dimension.height,
          dimension.pieces,
          this.selectedDimensionUnit === 'cm'
            ? MeasurementSystemEnum.METRIC
            : MeasurementSystemEnum.IMPERIAL,
        );
      })
      .reduce((acc, volume) => acc + volume, 0);
  }

  createDimensionGroup(): FormGroup {
    const { measurementSystem } = this.sessionService.getCurrentUser();
    return this.fb.group({
      pieces: [
        '',
        [
          FormValidators.required(),
          FormValidators.greaterThan(1),
          FormValidators.lessThan(1000),
        ],
      ],
      length: [
        '',
        [
          FormValidators.required(),
          FormValidators.greaterThan(0.1),
          FormValidators.lessThan(
            MeasurementUtil.convertMeasure(
              1000,
              MeasurementSystemEnum.METRIC,
              measurementSystem,
            ),
            this.selectedDimensionUnit,
          ),
        ],
      ],
      width: [
        '',
        [
          FormValidators.required(),
          FormValidators.greaterThan(0.1),
          FormValidators.lessThan(
            MeasurementUtil.convertMeasure(
              1000,
              MeasurementSystemEnum.METRIC,
              measurementSystem,
            ),
            this.selectedDimensionUnit,
          ),
        ],
      ],
      height: [
        '',
        [
          FormValidators.required(),
          FormValidators.greaterThan(0.1),
          FormValidators.lessThan(
            MeasurementUtil.convertMeasure(
              1000,
              MeasurementSystemEnum.METRIC,
              measurementSystem,
            ),
            this.selectedDimensionUnit,
          ),
        ],
      ],
      weight: [
        '',
        [
          FormValidators.required(),
          FormValidators.noDecimalPoint(),
          FormValidators.greaterThan(0.1),
          FormValidators.lessThan(
            MeasurementUtil.convertWeight(
              1000000,
              MeasurementSystemEnum.METRIC,
              measurementSystem,
            ),
            this.selectedDimensionWeightUnit,
          ),
        ],
      ],
      weightType: ['PER_PIECE', []],
    });
  }

  addDimensionRow(): void {
    this.dimensions.insert(0, this.createDimensionGroup());
  }

  removeDimensionRow(index: number): void {
    this.dimensions.removeAt(index);
  }

  get dimensions(): FormArray {
    return this.dimensionLoadTypeForm.get('dimensions') as FormArray;
  }

  getWeightUnitOptions() {
    return [
      { value: 'kg', label: 'kilogram(kg)' },
      { value: 'lb', label: 'pounds(lb)' },
    ];
  }

  getVolumeUnitOptions() {
    return [
      { value: 'm3', label: 'm3' },
      { value: 'ft3', label: 'ft3' },
    ];
  }

  getDimensionUnitOptions() {
    return [
      { value: 'cm', label: 'centimeter (cm)' },
      { value: 'inch', label: 'inch (in)' },
    ];
  }

  selectWeightUnitOption(value) {
    if (value === this.selectedWeightUnit) {
      return;
    }

    // Update the selected weight unit
    this.selectedWeightUnit = value;

    // Automatically set the corresponding volume unit
    this.selectedVolumeUnit = value === 'kg' ? 'm3' : 'ft3';
    this.updateVolumeUnit(this.selectedVolumeUnit);

    const weightControl = this.totalLoadTypeForm.get('weight');
    const currentWeight = weightControl.value;

    const convertedWeight = MeasurementUtil.convertWeight(
      currentWeight,
      value === 'kg'
        ? MeasurementSystemEnum.IMPERIAL
        : MeasurementSystemEnum.METRIC,
      value === 'kg'
        ? MeasurementSystemEnum.METRIC
        : MeasurementSystemEnum.IMPERIAL,
    );

    if (+convertedWeight > 0) {
      weightControl.setValue(convertedWeight);
    }

    weightControl.setValidators([
      FormValidators.required(),
      FormValidators.greaterThan(0.1),
      FormValidators.noDecimalPoint(),
      FormValidators.lessThan(
        MeasurementUtil.convertWeight(
          1000000,
          MeasurementSystemEnum.METRIC,
          value === 'kg'
            ? MeasurementSystemEnum.METRIC
            : MeasurementSystemEnum.IMPERIAL,
        ),
        value,
      ),
    ]);
    weightControl.updateValueAndValidity();
  }

  selectVolumeUnitOption(value) {
    if (value === this.selectedVolumeUnit) {
      return;
    }

    // Update the selected volume unit
    this.selectedVolumeUnit = value;

    // Automatically set the corresponding weight unit
    this.selectedWeightUnit = value === 'm3' ? 'kg' : 'lb';
    this.updateWeightUnit(this.selectedWeightUnit);

    const volumeControl = this.totalLoadTypeForm.get('volume');
    const currentVolume = volumeControl.value;

    const convertedVolume = MeasurementUtil.convertVolume(
      currentVolume,
      value === 'm3'
        ? MeasurementSystemEnum.IMPERIAL
        : MeasurementSystemEnum.METRIC,
      value === 'm3'
        ? MeasurementSystemEnum.METRIC
        : MeasurementSystemEnum.IMPERIAL,
    );

    if (+convertedVolume > 0) {
      volumeControl.setValue(convertedVolume);
    }

    volumeControl.setValidators([
      FormValidators.required(),
      FormValidators.greaterThan(0.0001),
      FormValidators.noDecimalPoint(),
      FormValidators.lessThan(
        MeasurementUtil.convertVolume(
          1000,
          MeasurementSystemEnum.METRIC,
          value === 'm3'
            ? MeasurementSystemEnum.METRIC
            : MeasurementSystemEnum.IMPERIAL,
        ),
        value,
      ),
    ]);
    volumeControl.updateValueAndValidity();
  }

  updateWeightUnit(value) {
    const weightControl = this.totalLoadTypeForm.get('weight');
    const currentWeight = weightControl.value;

    const convertedWeight = MeasurementUtil.convertWeight(
      currentWeight,
      value === 'kg'
        ? MeasurementSystemEnum.IMPERIAL
        : MeasurementSystemEnum.METRIC,
      value === 'kg'
        ? MeasurementSystemEnum.METRIC
        : MeasurementSystemEnum.IMPERIAL,
    );

    if (+convertedWeight > 0) {
      weightControl.setValue(convertedWeight);
    }

    weightControl.setValidators([
      FormValidators.required(),
      FormValidators.greaterThan(0.1),
      FormValidators.noDecimalPoint(),
      FormValidators.lessThan(
        MeasurementUtil.convertWeight(
          1000000,
          MeasurementSystemEnum.METRIC,
          value === 'kg'
            ? MeasurementSystemEnum.METRIC
            : MeasurementSystemEnum.IMPERIAL,
        ),
        value,
      ),
    ]);
    weightControl.updateValueAndValidity();
  }

  updateVolumeUnit(value) {
    const volumeControl = this.totalLoadTypeForm.get('volume');
    const currentVolume = volumeControl.value;

    const convertedVolume = MeasurementUtil.convertVolume(
      currentVolume,
      value === 'm3'
        ? MeasurementSystemEnum.IMPERIAL
        : MeasurementSystemEnum.METRIC,
      value === 'm3'
        ? MeasurementSystemEnum.METRIC
        : MeasurementSystemEnum.IMPERIAL,
    );

    if (+convertedVolume > 0) {
      volumeControl.setValue(convertedVolume);
    }

    volumeControl.setValidators([
      FormValidators.required(),
      FormValidators.greaterThan(0.0001),
      FormValidators.noDecimalPoint(),
      FormValidators.lessThan(
        MeasurementUtil.convertVolume(
          1000,
          MeasurementSystemEnum.METRIC,
          value === 'm3'
            ? MeasurementSystemEnum.METRIC
            : MeasurementSystemEnum.IMPERIAL,
        ),
        value,
      ),
    ]);
    volumeControl.updateValueAndValidity();
  }

  selectDimensionUnitOption(value) {
    if (value === this.selectedDimensionUnit) {
      return;
    }

    // Update the selected dimension unit
    this.selectedDimensionUnit = value;

    // Automatically set the corresponding weight unit
    this.selectedDimensionWeightUnit = value === 'cm' ? 'kg' : 'lb';
    this.updateDimensionWeightUnit(this.selectedDimensionWeightUnit);

    const dimensionsArray = this.dimensionLoadTypeForm.get(
      'dimensions',
    ) as FormArray;

    dimensionsArray.controls.forEach((group: FormGroup) => {
      ['length', 'width', 'height'].forEach((controlName) => {
        const control = group.get(controlName);
        if (control) {
          const currentValue = control.value;
          const convertedValue = MeasurementUtil.convertMeasure(
            currentValue,
            value === 'cm'
              ? MeasurementSystemEnum.IMPERIAL
              : MeasurementSystemEnum.METRIC,
            value === 'cm'
              ? MeasurementSystemEnum.METRIC
              : MeasurementSystemEnum.IMPERIAL,
          );
          if (+convertedValue) {
            control.setValue(convertedValue);
          }

          control.setValidators([
            FormValidators.required(),
            FormValidators.greaterThan(0.0001),
            FormValidators.lessThan(
              MeasurementUtil.convertMeasure(
                1000,
                MeasurementSystemEnum.METRIC,
                value === 'cm'
                  ? MeasurementSystemEnum.METRIC
                  : MeasurementSystemEnum.IMPERIAL,
              ),
              value,
            ),
          ]);

          control.updateValueAndValidity();
        }
      });
    });
  }

  selectDimensionWeightUnit(value) {
    if (value === this.selectedDimensionWeightUnit) {
      return;
    }

    // Update the selected weight unit
    this.selectedDimensionWeightUnit = value;

    // Automatically set the corresponding dimension unit
    this.selectedDimensionUnit = value === 'kg' ? 'cm' : 'inch';
    this.updateDimensionUnit(this.selectedDimensionUnit);

    const dimensionsArray = this.dimensionLoadTypeForm.get(
      'dimensions',
    ) as FormArray;

    dimensionsArray.controls.forEach((group: FormGroup) => {
      const weightControl = group.get('weight');
      if (weightControl) {
        const currentValue = weightControl.value;
        const convertedValue = MeasurementUtil.convertWeight(
          currentValue,
          value === 'kg'
            ? MeasurementSystemEnum.IMPERIAL
            : MeasurementSystemEnum.METRIC,
          value === 'kg'
            ? MeasurementSystemEnum.METRIC
            : MeasurementSystemEnum.IMPERIAL,
        );
        if (+convertedValue > 0) {
          weightControl.setValue(convertedValue);
        }

        weightControl.setValidators([
          FormValidators.required(),
          FormValidators.noDecimalPoint(),
          FormValidators.greaterThan(0.0001),
          FormValidators.lessThan(
            MeasurementUtil.convertWeight(
              1000000,
              MeasurementSystemEnum.METRIC,
              value === 'kg'
                ? MeasurementSystemEnum.METRIC
                : MeasurementSystemEnum.IMPERIAL,
            ),
            value,
          ),
        ]);

        weightControl.updateValueAndValidity();
      }
    });
  }

  updateDimensionUnit(value) {
    const dimensionsArray = this.dimensionLoadTypeForm.get(
      'dimensions',
    ) as FormArray;

    dimensionsArray.controls.forEach((group: FormGroup) => {
      ['length', 'width', 'height'].forEach((controlName) => {
        const control = group.get(controlName);
        if (control) {
          const currentValue = control.value;
          const convertedValue = MeasurementUtil.convertMeasure(
            currentValue,
            value === 'cm'
              ? MeasurementSystemEnum.IMPERIAL
              : MeasurementSystemEnum.METRIC,
            value === 'cm'
              ? MeasurementSystemEnum.METRIC
              : MeasurementSystemEnum.IMPERIAL,
          );
          if (+convertedValue) {
            control.setValue(convertedValue);
          }

          control.setValidators([
            FormValidators.required(),
            FormValidators.greaterThan(0.0001),
            FormValidators.lessThan(
              MeasurementUtil.convertMeasure(
                1000,
                MeasurementSystemEnum.METRIC,
                value === 'cm'
                  ? MeasurementSystemEnum.METRIC
                  : MeasurementSystemEnum.IMPERIAL,
              ),
              value,
            ),
          ]);

          control.updateValueAndValidity();
        }
      });
    });
  }

  updateDimensionWeightUnit(value) {
    const dimensionsArray = this.dimensionLoadTypeForm.get(
      'dimensions',
    ) as FormArray;

    dimensionsArray.controls.forEach((group: FormGroup) => {
      const weightControl = group.get('weight');
      if (weightControl) {
        const currentValue = weightControl.value;
        const convertedValue = MeasurementUtil.convertWeight(
          currentValue,
          value === 'kg'
            ? MeasurementSystemEnum.IMPERIAL
            : MeasurementSystemEnum.METRIC,
          value === 'kg'
            ? MeasurementSystemEnum.METRIC
            : MeasurementSystemEnum.IMPERIAL,
        );
        if (+convertedValue > 0) {
          weightControl.setValue(convertedValue);
        }

        weightControl.setValidators([
          FormValidators.required(),
          FormValidators.noDecimalPoint(),
          FormValidators.greaterThan(0.0001),
          FormValidators.lessThan(
            MeasurementUtil.convertWeight(
              1000000,
              MeasurementSystemEnum.METRIC,
              value === 'kg'
                ? MeasurementSystemEnum.METRIC
                : MeasurementSystemEnum.IMPERIAL,
            ),
            value,
          ),
        ]);

        weightControl.updateValueAndValidity();
      }
    });
  }

  togglePopover(popoverType: string) {
    switch (popoverType) {
      case 'weight':
        this.weightUnitPopoverVisible = !this.weightUnitPopoverVisible;
        this.managePopover(
          this.weightUnitPopoverVisible,
          this.weightUnitInput?.nativeElement,
          this.weightUnitPopover?.nativeElement,
          (instance: any) => {
            this.weightUnitPopperInstance = instance;
          },
        );
        break;
      case 'volume':
        this.volumeUnitPopoverVisible = !this.volumeUnitPopoverVisible;
        this.managePopover(
          this.volumeUnitPopoverVisible,
          this.volumeUnitInput?.nativeElement,
          this.volumeUnitPopover?.nativeElement,
          (instance: any) => {
            this.volumeUnitPopperInstance = instance;
          },
        );
        break;
    }
  }

  private closePopoverIfClickedOutside(
    event: Event,
    filterElement: any,
    popoverElement: any,
    isPopoverVisible: boolean,
    closePopover: () => void,
  ) {
    const clickedInsideFilter = filterElement?.contains(event.target),
      clickedInsidePopover = popoverElement?.contains(event.target),
      clickedInsideCalendar = (event.target as HTMLElement).classList.contains(
        'mat-calendar-body-cell-content',
      );
    if (
      !clickedInsideFilter &&
      !clickedInsidePopover &&
      !clickedInsideCalendar &&
      isPopoverVisible
    ) {
      closePopover();
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    this.closePopoverIfClickedOutside(
      event,
      this.weightUnitInput?.nativeElement,
      this.weightUnitPopover?.nativeElement,
      this.weightUnitPopoverVisible,
      () => {
        this.weightUnitPopoverVisible = false;
        if (this.weightUnitPopperInstance) {
          this.weightUnitPopperInstance.destroy();
        }
      },
    );

    this.closePopoverIfClickedOutside(
      event,
      this.volumeUnitInput?.nativeElement,
      this.volumeUnitPopover?.nativeElement,
      this.volumeUnitPopoverVisible,
      () => {
        this.volumeUnitPopoverVisible = false;
        if (this.volumeUnitPopperInstance) {
          this.volumeUnitPopperInstance.destroy();
        }
      },
    );
  }

  private managePopover(
    isVisible: boolean,
    filterElement: any,
    popoverElement: any,
    setInstance: (instance: any) => void,
  ) {
    if (isVisible) {
      const instance = createPopper(filterElement, popoverElement, {
        placement: 'top-start',
      });
      setInstance(instance);
    } else {
      setInstance(null);
    }
  }

  cancel() {
    this.activeModal.close(false);
  }

  getError(field: string): string {
    if (
      !this.totalLoadTypeForm?.get(field)?.touched &&
      !this.totalLoadTypeForm?.get(field)?.dirty
    ) {
      return null;
    }
    const control = this.totalLoadTypeForm?.controls[field];
    if (!!control.errors && Object.values(control.errors).length > 0) {
      return Object.values(control.errors)[0]?.message?.replace(
        '{field}',
        this.getErrorField(field),
      );
    }
    return null;
  }

  getTotalErrors(): string[] {
    if (!this.totalLoadTypeForm) {
      return [];
    }

    const errors: string[] = [];

    Object.keys(this.totalLoadTypeForm.controls).forEach((field) => {
      const control = this.totalLoadTypeForm.get(field);

      if (control && (control.touched || control.dirty) && control.errors) {
        Object.values(control.errors).forEach((error) => {
          const errorMessage = (error as any)?.message?.replace(
            '{field}',
            this.getErrorField(field),
          );
          if (errorMessage) {
            errors.push(errorMessage);
          }
        });
      }
    });

    return errors;
  }

  getErrorField(field: string) {
    switch (field) {
      case 'weight':
        return $localize`:@@load-type.weight:Weight`;
      case 'volume':
        return $localize`:@@load-type.volume:Volume`;
      case 'pieces':
        return $localize`:@@load-type.pieces:Pieces`;
    }
    return '';
  }

  getDimensionErrors(index: number): Array<{ message: string }> {
    const dimensionGroup = this.dimensions.at(index) as FormGroup;

    if (!dimensionGroup) {
      return [];
    }

    const errorsList = Object.keys(dimensionGroup.controls).flatMap((field) => {
      const control = dimensionGroup.get(field);
      if (
        control &&
        (control.touched || control.dirty) &&
        control.errors &&
        Object.values(control.errors).length > 0
      ) {
        return Object.values(control.errors).map((error) => ({
          message: error?.message?.replace(
            '{field}',
            this.getDimensionErrorField(field),
          ),
        }));
      }
      return [];
    });

    return errorsList;
  }

  getDimensionErrorField(field: string): string {
    switch (field) {
      case 'pieces':
        return $localize`:@@load-type.pieces:Pieces`;
      case 'length':
        return $localize`:@@load-type.length:Length`;
      case 'width':
        return $localize`:@@load-type.width:Width`;
      case 'height':
        return $localize`:@@load-type.height:Height`;
      case 'weight':
        return $localize`:@@load-type.weight:Weight`;
    }
    return '';
  }

  getDimensionError(field: string, index?: number): string {
    const dimensionControl = (this.dimensions.at(index) as FormGroup)?.get(
      field,
    );
    if (!dimensionControl?.touched && !dimensionControl?.dirty) {
      return null;
    }

    if (
      !!dimensionControl.errors &&
      Object.values(dimensionControl.errors).length > 0
    ) {
      return Object.values(dimensionControl.errors)[0]?.message?.replace(
        '{field}',
        this.getDimensionErrorField(field),
      );
    }
  }

  sanitizeValues(data: any): any {
    if (Array.isArray(data)) {
      return data.map((item) => this.sanitizeValues(item));
    } else if (typeof data === 'object' && data !== null) {
      const sanitized: any = {};
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          sanitized[key] = this.sanitizeValues(data[key]);
        }
      }
      return sanitized;
    } else if (typeof data === 'string' && !Number.isNaN(Number(data))) {
      return data.replace(/^0+/, '') || '0';
    }
    return data;
  }

  async handleCalculate(): Promise<void> {
    if (this.isCalculateButtonDisabled) {
      return;
    }
    try {
      const totalLoadData = this.totalLoadTypeForm.value;
      const dimensionLoadData = this.dimensionLoadTypeForm.value;

      const formData: any = {
        selectedWeightUnit: this.selectedWeightUnit,
        selectedVolumeUnit: this.selectedVolumeUnit,
        selectedDimensionUnit: this.selectedDimensionUnit,
        selectedDimensionWeightUnit: this.selectedDimensionWeightUnit,
      };

      if (this.loadType === 'BY_VOLUME') {
        formData.totalLoadTypeForm = this.sanitizeValues(totalLoadData);
      } else {
        formData.dimensionLoadTypeForm = this.sanitizeValues(dimensionLoadData);
      }

      await this.indexedDbService.saveFormData('shipmentDimensions', formData);

      this.previousData = cloneDeep(formData);
      this.activeModal.close({
        chargeableWeight: this.calculateChargeableWeight(),
        pieces: this.dimensionPieces(false),
        volume: this.dimensionVolume(false),
        weight: this.dimensionWeight(false),
      });
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Failed to save form data:', error);
    }
  }

  async loadRecentSearch() {
    try {
      if (this.previousData) {
        if (this.previousData.totalLoadTypeForm) {
          this.loadType = 'BY_VOLUME';
          this.totalLoadTypeForm.patchValue(
            this.previousData.totalLoadTypeForm,
          );
        }
        if (this.previousData.dimensionLoadTypeForm) {
          this.loadType = 'BY_DIMENSION';

          const dimensionsArray = this.dimensionLoadTypeForm.get(
            'dimensions',
          ) as FormArray;
          dimensionsArray.clear();

          this.previousData.dimensionLoadTypeForm.dimensions.forEach(
            (dimension) => {
              dimensionsArray.push(this.createDimensionGroup());
            },
          );

          this.dimensionLoadTypeForm.patchValue(
            this.previousData.dimensionLoadTypeForm,
          );
        }

        this.selectedWeightUnit = this.previousData.selectedWeightUnit;
        this.selectedVolumeUnit = this.previousData.selectedVolumeUnit;
        this.selectedDimensionUnit = this.previousData.selectedDimensionUnit;
        this.selectedDimensionWeightUnit =
          this.previousData.selectedDimensionWeightUnit;
      }
    } catch (error) {
      console.error('Failed to load form data:', error);
    }
  }

  dimensionPieces(recentSearch: boolean): string {
    if (recentSearch) {
      if (this.previousData?.totalLoadTypeForm) {
        return +this.previousData?.totalLoadTypeForm?.pieces > 1
          ? `${this.previousData?.totalLoadTypeForm?.pieces} PCS`
          : `${this.previousData?.totalLoadTypeForm?.pieces} PC`;
      } else {
        const totalPieces =
          this.previousData?.dimensionLoadTypeForm?.dimensions?.reduce(
            (acc, dimension) => acc + +dimension?.pieces,
            0,
          );

        return totalPieces > 1 ? `${totalPieces} PCS` : `${totalPieces} PC`;
      }
    } else {
      if (this.loadType === 'BY_VOLUME') {
        const pieces = this.totalLoadTypeForm?.get('pieces').value;

        return +pieces > 1 ? `${pieces} PCS` : `${pieces} PC`;
      } else {
        const dimensionData =
          this.dimensionLoadTypeForm?.get('dimensions').value;
        const totalPieces = dimensionData?.reduce(
          (acc, dimension) => acc + +dimension?.pieces,
          0,
        );

        return totalPieces > 1 ? `${totalPieces} PCS` : `${totalPieces} PC`;
      }
    }
  }

  dimensionVolume(recentSearch?: boolean): string {
    if (recentSearch) {
      if (this.previousData?.totalLoadTypeForm) {
        return `${this.previousData?.totalLoadTypeForm?.volume?.toLocaleString(
          'en-US',
          {
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
          },
        )} ${this.previousData.selectedVolumeUnit}`;
      } else {
        return `${this.previousData?.dimensionLoadTypeForm?.dimensions
          ?.map((dimension) => {
            return MeasurementUtil.calculateVolume(
              dimension.width,
              dimension.length,
              dimension.height,
              dimension.pieces,
              this.previousData?.selectedDimensionUnit === 'cm'
                ? MeasurementSystemEnum.METRIC
                : MeasurementSystemEnum.IMPERIAL,
            );
          })
          .reduce((acc, volume) => acc + volume, 0)
          ?.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
          })} ${this.previousData?.selectedDimensionUnit === 'cm' ? 'm3' : 'ft3'}`;
      }
    } else {
      if (this.loadType === 'BY_VOLUME') {
        return `${this.totalLoadTypeForm
          ?.get('volume')
          ?.value?.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
          })} ${this.selectedVolumeUnit}`;
      } else {
        const dimensionData =
          this.dimensionLoadTypeForm?.get('dimensions').value;
        return `${dimensionData
          ?.map((dimension) => {
            return MeasurementUtil.calculateVolume(
              dimension.width,
              dimension.length,
              dimension.height,
              dimension.pieces,
              this.selectedDimensionUnit === 'cm'
                ? MeasurementSystemEnum.METRIC
                : MeasurementSystemEnum.IMPERIAL,
            );
          })
          .reduce((acc, volume) => acc + volume, 0)
          ?.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
          })} ${this.selectedDimensionUnit === 'cm' ? 'm3' : 'ft3'}`;
      }
    }
  }

  dimensionWeight(recentSearch?: boolean): string {
    if (recentSearch) {
      if (this.previousData?.totalLoadTypeForm) {
        return `${this.previousData?.totalLoadTypeForm?.weight?.toLocaleString(
          'en-US',
          {
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
          },
        )} ${this.previousData.selectedWeightUnit}`;
      } else {
        return `${this.previousData?.dimensionLoadTypeForm?.dimensions
          ?.reduce((acc, dimension) => acc + +dimension?.weight, 0)
          ?.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
          })} ${this.previousData?.selectedDimensionWeightUnit}`;
      }
    } else {
      if (this.loadType === 'BY_VOLUME') {
        return `${this.totalLoadTypeForm
          ?.get('weight')
          ?.value?.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
          })} ${this.selectedWeightUnit}`;
      } else {
        const dimensionData =
          this.dimensionLoadTypeForm?.get('dimensions').value;
        return `${dimensionData
          ?.reduce((acc, dimension) => acc + +dimension?.weight, 0)
          ?.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
          })} ${this.selectedDimensionWeightUnit}`;
      }
    }
  }

  get isCalculateButtonDisabled(): boolean {
    if (
      (this.loadType === 'BY_VOLUME'
        ? this.totalLoadTypeForm?.invalid
        : this.dimensionLoadTypeForm?.invalid) ||
      this.isVolumeLow
    ) {
      return true;
    }
    return false;
  }

  deleteRecentSearches() {
    this.indexedDbService.deleteFormData('shipmentDimensions');
    this.previousData = null;
    this.cdr.detectChanges();
  }

  onClickRecentSearch() {
    this.loadRecentSearch();
  }

  calculateChargeableWeight() {
    const weight = this.dimensionWeight()?.split(' ')[0],
      volume = this.dimensionVolume()?.split(' ')[0];
    if (weight && volume && weight !== '.' && volume !== '.') {
      let chargeableWeight = null,
        weightConvert: MeasurementSystemEnum[] = [];
      const convertedWeightUnit =
        this.loadType === 'BY_VOLUME'
          ? this.selectedWeightUnit
          : this.selectedDimensionWeightUnit;
      if (convertedWeightUnit === 'lb') {
        weightConvert = [
          MeasurementSystemEnum.IMPERIAL,
          MeasurementSystemEnum.METRIC,
        ];
      }
      chargeableWeight = this.getChargeableWeight(weightConvert);
      return chargeableWeight?.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      });
    }
  }

  getChargeableWeight(weightConversion: MeasurementSystemEnum[]): number {
    let totalVolume = +this.dimensionVolume()?.split(' ')[0],
      totalWeight = +this.dimensionWeight()?.split(' ')[0];
    const volumeUnit =
      this.loadType === 'BY_VOLUME'
        ? this.selectedVolumeUnit
        : this.selectedDimensionUnit === 'cm'
          ? 'm3'
          : 'ft3';
    if (volumeUnit === 'ft3') {
      totalVolume = MeasurementUtil.convertVolume(
        totalVolume,
        MeasurementSystemEnum.IMPERIAL,
        MeasurementSystemEnum.METRIC,
      );
    }
    if (weightConversion?.length === 2) {
      totalWeight = MeasurementUtil.convertWeight(
        totalWeight,
        weightConversion[0],
        weightConversion[1],
      );
    }
    let chargeableWeight = MeasurementUtil.calculateChargeableWeight(
      totalVolume,
      totalWeight,
      MeasurementSystemEnum.METRIC,
    );
    if (weightConversion?.length === 2) {
      chargeableWeight = MeasurementUtil.convertWeight(
        chargeableWeight,
        weightConversion[1],
        weightConversion[0],
      );
    }

    return chargeableWeight;
  }
}
